/**
 * APC Commission Program — public route /esquemadecomisiones
 *
 * Page goal: in under 30 seconds, a potential collaborator understands how
 * much they can earn by referring an investor to APC.
 *
 * Calculator rules (from the spec):
 *   • Plazo 6m  → utilidad APC = 20% del monto
 *   • Plazo 12m → utilidad APC = 30% del monto
 *   • Monto <  $10,000  → comisión = 20% de la utilidad APC
 *   • Monto >= $10,000  → comisión = 25% de la utilidad APC
 *   • Utilidad neta APC = utilidad APC - comisión
 *
 * Palette: Azul oscuro #0A2342, blanco, verde financiero #2ECC71, dorado #FFD700.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  CurrencyDollar, ArrowRight, X, Handshake, Lightning, MapPin, ChartLineUp,
  CheckCircle, Trophy, Users, Calculator,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function calcCommission(amount, term) {
  const apcPct = term === 12 ? 0.30 : 0.20;
  const commPct = amount >= 10000 ? 0.25 : 0.20;
  const utilidadAPC = amount * apcPct;
  const comision = utilidadAPC * commPct;
  return { apcPct, commPct, utilidadAPC, comision, neta: utilidadAPC - comision };
}

const EXAMPLES = [
  { amount: 5000,  term: 6 },
  { amount: 5000,  term: 12 },
  { amount: 10000, term: 6 },
  { amount: 10000, term: 12 },
  { amount: 20000, term: 12 },
  { amount: 50000, term: 12 },
];

export default function CommissionLanding() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Esquema de Comisiones — APC";
  }, []);

  return (
    <div className="min-h-screen bg-white text-zinc-900 antialiased">
      {/* HERO */}
      <header className="relative bg-[#0A2342] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ backgroundImage: "radial-gradient(ellipse at top right, #FFD700 0%, transparent 50%)" }} />
        <nav className="relative max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <a href="/" className="font-heading font-extrabold text-xl tracking-tight hover:opacity-80 transition" data-testid="nav-logo-home">APC <span className="font-mono text-xs opacity-60 ml-1">TECNOLOGÍA</span></a>
          <a href="#calc" className="hidden sm:inline-flex text-xs uppercase tracking-widest text-white/80 hover:text-white">Calcular</a>
        </nav>
        <div className="relative max-w-7xl mx-auto px-6 py-20 sm:py-28">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700] text-xs font-mono uppercase tracking-widest mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FFD700] animate-pulse"/> Programa de colaboradores
            </div>
            <h1 className="font-heading font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-4xl">
              Gana comisiones ayudando a <span className="text-[#FFD700]">APC</span> a crecer.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-white/75 max-w-2xl leading-relaxed">
              Conecta inversionistas con APC y recibe comisiones atractivas por cada inversión concretada. Una sola recomendación puede convertirse en cientos o miles de dólares para ti.
            </p>
            <p className="mt-2 text-base text-white/60 max-w-2xl">No necesitas vender productos, solo conectar personas con una oportunidad real de inversión.</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#calc" className="inline-flex items-center gap-2 px-7 h-14 rounded-full bg-[#FFD700] hover:bg-[#e5c000] text-[#0A2342] font-bold text-sm shadow-lg shadow-[#FFD700]/30 transition-colors">
                <Calculator size={16} weight="bold" /> Calcular mi comisión
              </a>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-xs text-white/60 font-mono uppercase tracking-widest">
              <span className="inline-flex items-center gap-2"><Lightning size={12} weight="fill" className="text-[#FFD700]"/> Gana por conectar</span>
              <span className="inline-flex items-center gap-2"><Trophy size={12} weight="fill" className="text-[#FFD700]"/> Sin límite de comisiones</span>
              <span className="inline-flex items-center gap-2"><CurrencyDollar size={12} weight="fill" className="text-[#2ECC71]"/> Ingresos en dólares</span>
            </div>
          </motion.div>
        </div>
      </header>

      {/* CALCULATOR */}
      <CalculatorSection />

      {/* EJEMPLOS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#2ECC71] mb-3">Ejemplos rápidos</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mb-10 max-w-3xl">Mira lo que puedes ganar.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {EXAMPLES.map((e, i) => {
              const r = calcCommission(e.amount, e.term);
              return (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="rounded-2xl border-2 border-zinc-100 bg-white p-6 hover:border-[#FFD700] transition-colors">
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Inversión {fmt(e.amount)}</div>
                  <div className="mt-1 text-sm text-zinc-600">Plazo: {e.term} meses</div>
                  <div className="mt-6 pt-6 border-t border-zinc-100">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#2ECC71]">Tu comisión</div>
                    <div className="font-heading text-4xl font-extrabold text-[#0A2342]">{fmt(r.comision)}</div>
                    <div className="text-[10px] text-zinc-400 mt-1">USD</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MOTIVACIONAL */}
      <section className="bg-[#0A2342] text-white">
        <div className="max-w-5xl mx-auto px-6 py-20 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">Convierte tus contactos en ingresos reales.</h2>
          <p className="mt-6 text-lg text-white/75 max-w-3xl mx-auto leading-relaxed">
            Todos conocemos personas que buscan hacer crecer su dinero, pero no siempre saben dónde empezar. Con APC puedes ayudarlas a conocer una empresa tecnológica con visión de crecimiento, mientras tú recibes una comisión por cada inversionista que integres exitosamente.
          </p>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section className="bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#2ECC71] mb-3">Cómo funciona</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mb-12">4 pasos. Sin complicaciones.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { n: 1, t: "Prospectas",       d: "Identificas a un posible socio inversionista dentro de tu red cercana." },
              { n: 2, t: "Recibe información", d: "El inversionista recibe la información correspondiente del esquema APC." },
              { n: 3, t: "Hacemos el meet",   d: "Nos reunimos inversor, representante de APC y tú para concretar la inversión." },
              { n: 4, t: "Cobras tu comisión", d: "Recibes tu comisión cuando la inversión se concreta." },
            ].map((s) => (
              <div key={s.n} className="bg-white rounded-2xl border border-zinc-200 p-6 relative">
                <div className="absolute -top-4 -left-2 w-10 h-10 rounded-full bg-[#FFD700] text-[#0A2342] font-heading font-extrabold flex items-center justify-center text-lg shadow-lg">{s.n}</div>
                <div className="font-heading text-lg font-bold mt-3">{s.t}</div>
                <div className="text-sm text-zinc-600 mt-2 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFICIOS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#2ECC71] mb-3">Beneficios</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mb-10 max-w-3xl">Hecho para ti, sin barreras.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl">
            {[
              "No necesitas invertir dinero.",
              "No necesitas comprar inventario.",
              "No tienes horarios fijos.",
              "Puedes recomendar desde tus contactos.",
              "Puedes generar ingresos en dólares.",
              "No hay límite de comisiones.",
              "Entre mayor sea la inversión, mayor será tu ganancia.",
            ].map((b) => (
              <div key={b} className="flex items-start gap-3 bg-zinc-50 border border-zinc-100 rounded-xl px-4 py-3">
                <CheckCircle size={18} weight="fill" className="text-[#2ECC71] shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-700">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FRASES */}
      <section className="bg-gradient-to-br from-[#0A2342] to-[#0a1a38] text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          {[
            { i: Handshake,     t: "Gana por conectar." },
            { i: Users,         t: "Tu red puede convertirse en ingresos." },
            { i: Lightning,     t: "Una recomendación puede cambiar tu mes." },
            { i: ChartLineUp,   t: "Mientras APC crece, tú también ganas." },
          ].map((f) => (
            <div key={f.t}>
              <f.i size={28} weight="duotone" className="text-[#FFD700] mx-auto mb-3" />
              <p className="font-heading text-lg font-semibold leading-tight">{f.t}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL — closed program, no public registration */}
      <section className="bg-white text-center py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0A2342]">¿Listo para empezar a ganar?</h2>
          <p className="mt-4 text-zinc-600 text-lg">Este programa está dirigido a una red cercana de contactos APC. Para activarte, contacta directamente al equipo APC.</p>
        </div>
      </section>

      <footer className="bg-[#06122B] text-white/50 py-8 text-center text-xs">
        <div className="space-y-3">
          <div>© {new Date().getFullYear()} APC Tecnología</div>
          <div className="flex items-center justify-center gap-4 text-[10px] uppercase tracking-widest font-mono">
            <a href="/esquemadeinversion" className="text-white/40 hover:text-[#FFD700] transition-colors" data-testid="footer-link-invest">Invierte</a>
            <span className="text-white/20">·</span>
            <a href="/esquemadecomisiones" className="text-white/40 hover:text-[#FFD700] transition-colors" data-testid="footer-link-connect">Conéctanos</a>
          </div>
        </div>
      </footer>

      {null}
    </div>
  );
}

function CalculatorSection() {
  const [amount, setAmount] = useState(10000);
  const [term, setTerm] = useState(6);
  const r = useMemo(() => calcCommission(Number(amount) || 0, term), [amount, term]);

  return (
    <section id="calc" className="bg-zinc-50">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#2ECC71] mb-3">Calculadora</div>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-10 max-w-3xl">¿Cuánto puedes ganar?</h2>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Inputs */}
          <div className="lg:col-span-3 bg-white rounded-3xl border border-zinc-200 p-8 space-y-6">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Monto de inversión (USD)</label>
              <input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)}
                     className="mt-1 w-full text-3xl font-heading font-extrabold border-b-2 border-zinc-200 focus:border-[#2ECC71] outline-none py-2 numeric"
                     data-testid="comm-amount" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Plazo</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {[6, 12].map((t) => (
                  <button key={t} type="button" onClick={() => setTerm(t)}
                          className={`px-6 h-11 rounded-full border text-sm font-semibold ${term === t ? "bg-[#0A2342] text-white border-[#0A2342]" : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-500"}`}
                          data-testid={`comm-term-${t}`}>{t} meses</button>
                ))}
              </div>
            </div>
            <div className="pt-4 border-t border-zinc-100 text-xs text-zinc-500 space-y-1">
              <div className="flex justify-between"><span>Utilidad APC ({(r.apcPct * 100).toFixed(0)}%)</span><b className="text-zinc-700 numeric">{fmt(r.utilidadAPC)}</b></div>
              <div className="flex justify-between"><span>% de comisión aplicado</span><b className="text-zinc-700">{(r.commPct * 100).toFixed(0)}%</b></div>
              <div className="flex justify-between"><span>Utilidad neta APC</span><b className="text-zinc-700 numeric">{fmt(r.neta)}</b></div>
            </div>
          </div>
          {/* Results */}
          <div className="lg:col-span-2 bg-[#0A2342] text-white rounded-3xl p-8 flex flex-col justify-between">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#FFD700]">Tu comisión estimada</div>
              <div className="font-heading text-5xl sm:text-6xl font-extrabold text-[#FFD700] leading-none numeric mt-3" data-testid="comm-result">{fmt(r.comision)}</div>
              <div className="text-xs text-white/50 mt-2">USD</div>
            </div>
            <div className="mt-8 space-y-1 text-xs text-white/60">
              <Row label="Monto invertido"   value={fmt(Number(amount) || 0)} />
              <Row label="Plazo seleccionado" value={`${term} meses`} />
              <Row label="Utilidad APC"      value={fmt(r.utilidadAPC)} />
              <Row label="% de comisión"     value={`${(r.commPct * 100).toFixed(0)}%`} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1 border-b border-white/10 last:border-0">
      <span className="uppercase tracking-widest font-mono text-white/50">{label}</span>
      <span className="font-semibold text-sm text-white">{value}</span>
    </div>
  );
}

function CollabModal({ onClose }) {
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", city: "", has_prospects: false, message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.full_name.trim() || !form.phone.trim() || !form.email.trim()) {
      setError("Por favor completa los campos obligatorios.");
      return;
    }
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/api/public/collaborators`, form);
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "No pudimos enviar tu solicitud.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="collab-modal">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                  className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center z-10" data-testid="collab-modal-close">
          <X size={18} />
        </button>
        {!sent ? (
          <form onSubmit={submit} className="p-8">
            <div className="text-xs font-mono uppercase tracking-widest text-[#2ECC71] mb-2">Registro</div>
            <h3 className="font-heading text-2xl font-extrabold mb-1">Quiero ser colaborador</h3>
            <p className="text-sm text-zinc-500 mb-6">Un asesor de APC te contactará para activarte en el programa.</p>
            <div className="space-y-3">
              <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Nombre completo *"
                     className="w-full h-12 px-4 border border-zinc-300 rounded-full focus:border-[#0A2342] outline-none text-sm" data-testid="collab-name" />
              <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Celular *" type="tel"
                     className="w-full h-12 px-4 border border-zinc-300 rounded-full focus:border-[#0A2342] outline-none text-sm" data-testid="collab-phone" />
              <input required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Correo electrónico *" type="email"
                     className="w-full h-12 px-4 border border-zinc-300 rounded-full focus:border-[#0A2342] outline-none text-sm" data-testid="collab-email" />
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ciudad"
                     className="w-full h-12 px-4 border border-zinc-300 rounded-full focus:border-[#0A2342] outline-none text-sm" data-testid="collab-city" />
              <div className="px-4 py-2 border border-zinc-300 rounded-2xl">
                <div className="text-xs text-zinc-500 mb-1.5">¿Tienes posibles inversionistas?</div>
                <div className="inline-flex rounded-full bg-zinc-100 p-1 text-xs font-semibold">
                  {[{v:true,l:"Sí"},{v:false,l:"No"}].map((o) => (
                    <button key={o.l} type="button" onClick={() => setForm({ ...form, has_prospects: o.v })}
                            className={`px-4 py-1 rounded-full ${form.has_prospects === o.v ? "bg-white text-[#0A2342] shadow-sm" : "text-zinc-500"}`}
                            data-testid={`collab-pros-${o.l}`}>{o.l}</button>
                  ))}
                </div>
              </div>
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Mensaje opcional" rows={2}
                        className="w-full px-4 py-3 border border-zinc-300 rounded-2xl focus:border-[#0A2342] outline-none text-sm" data-testid="collab-message" />
            </div>
            {error && <div className="mt-3 text-sm text-[#E0000F]">{error}</div>}
            <button type="submit" disabled={loading}
                    className="mt-6 w-full h-12 rounded-full bg-[#2ECC71] hover:bg-[#27ae60] text-white font-semibold text-sm transition-colors disabled:opacity-60"
                    data-testid="collab-submit">
              {loading ? "Enviando…" : "Enviar solicitud"}
            </button>
          </form>
        ) : (
          <div className="p-10 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#2ECC71]/10 flex items-center justify-center text-[#2ECC71] mb-4">
              <CheckCircle size={32} weight="fill" />
            </div>
            <h3 className="font-heading text-2xl font-extrabold mb-1">¡Bienvenido al equipo APC!</h3>
            <p className="text-sm text-zinc-600 mt-3">Recibimos tu solicitud. Un asesor te contactará pronto para activarte como colaborador.</p>
            <button onClick={onClose} className="mt-8 px-6 h-11 rounded-full bg-[#0A2342] hover:bg-[#0A1A38] text-white font-semibold text-sm" data-testid="collab-success-close">Cerrar</button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
